import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore, useAppointmentsStore, useSettingsStore } from '@/stores';
import * as api from '@/lib/api';
import {
  getAppointmentsByDate,
  saveAppointments,
  syncAppointmentsForDate,
  getCachedBookingDetails,
  saveBookingDetails,
  type CachedBookingDetail,
} from '@/lib/db';
import { buildDatesWindow, today } from '@/utils/date';
import { buildWhatsappUrl } from '@/utils/whatsapp';
import { messageForAppointment } from '@/utils/message';
import {
  consolidateBookings,
  getFilteredItems,
  getSortedItems,
  getGroupedItems,
} from '@/utils/appointmentGrouping';
export { getFilteredItems, getSortedItems, getGroupedItems, consolidateBookings };
import type { Appointment } from '@/types/appointments';
import type { FreshaGraphQLBooking } from '@/types/fresha';
import type { Settings } from '@/types/settings';

export function useAppointments() {
  const { workspaceId, locationId, locations, employees } = useAuthStore();
  const settings = useSettingsStore();
  const {
    items, loading, selectedDate, searchQuery, filterStatus,
    filterEmployee, filterLocation,
    sortBy, sortOrder, groupBy, processedIds,
    setItems, setLoading, setSelectedDate,
    setSearchQuery, setFilterStatus, setFilterEmployee, setFilterLocation,
    setSortBy, setSortOrder, setGroupBy,
    markProcessed, unmarkProcessed, isProcessed,
  } = useAppointmentsStore();

  const abortRef = useRef<AbortController | null>(null);

  const fetchAppointments = useCallback(async (date?: string) => {
    const targetDate = date || selectedDate || today();
    if (!workspaceId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // 1. Stale-While-Revalidate: Immediate local render from Dexie (0ms load)
    try {
      const cachedAppointments = await getAppointmentsByDate(workspaceId, targetDate, locationId);
      if (!controller.signal.aborted && useAppointmentsStore.getState().selectedDate === targetDate) {
        setItems(cachedAppointments);
      }
    } catch (cacheErr) {
      console.warn('Failed to load cached appointments from Dexie:', cacheErr);
    }

    setLoading(true);
    try {
      const dates = buildDatesWindow(targetDate);
      const isAllLocations = !locationId || locationId === 'all';
      const targetLocations = isAllLocations && locations.length > 0
        ? locations.map((l) => String(l.id))
        : [String(locationId || locations[0]?.id || '')].filter(Boolean);

      const allBookings: FreshaGraphQLBooking[] = [];
      const collectedEmployees: any[] = [...(employees || [])];
      let fetchSuccessCount = 0;

      await Promise.all(
        targetLocations.map(async (lid) => {
          try {
            if (controller.signal.aborted) return;

            // Get employees for this location
            let locEmployees = lid === locationId ? employees : [];
            if (!locEmployees || locEmployees.length === 0) {
              const empRes = await api.getEmployees(workspaceId, lid);
              locEmployees = Array.isArray(empRes?.data) ? empRes.data : [];
            }
            // Add to collected employees if not present
            for (const emp of locEmployees) {
              if (emp?.id && !collectedEmployees.some((e) => String(e.id) === String(emp.id))) {
                collectedEmployees.push(emp);
              }
            }

            const resources = (locEmployees || [])
              .filter((e: any) => e.type === 'employees' || e.type === 'EMPLOYEES' || !e.type)
              .map((e: any) => ({
                id: String(e.id),
                type: 'EMPLOYEES',
              }));

            const response = await api.getAppointments({
              workspaceId,
              dates,
              locationId: lid,
              resources,
              withCancelled: true,
            });

            const dailyBookings = response?.data?.dailyCalendarBookings || [];
            for (const day of dailyBookings) {
              if (day.bookings) {
                allBookings.push(...day.bookings);
              }
            }
            fetchSuccessCount++;
          } catch (locErr) {
            console.error(`Failed to fetch appointments for location ${lid}:`, locErr);
          }
        })
      );

      // Guard: If aborted or user selected a different date in the meantime, ignore network response
      if (controller.signal.aborted || useAppointmentsStore.getState().selectedDate !== targetDate) {
        return;
      }

      // Only update and persist if at least one location query succeeded
      if (fetchSuccessCount > 0) {
        // Consolidate bookings into domain appointments with location resolution
        const rawAppointments = consolidateBookings(
          allBookings,
          collectedEmployees,
          locations,
          settings.locationAliases,
          settings.locationNameMode
        );
        const appointments = rawAppointments
          .map((apt) => ({
            ...apt,
            workspaceId,
            date: apt.date || targetDate,
          }))
          .filter((apt) => apt.date === targetDate);

        setItems(appointments);
        await syncAppointmentsForDate(workspaceId, targetDate, locationId, appointments);

        // Enrich with customer details in background
        enrichAppointments(appointments, workspaceId, settings, targetDate, controller.signal);
      }
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      // Resilient error handling: keep cached items intact on failure
    } finally {
      if (!controller.signal.aborted && useAppointmentsStore.getState().selectedDate === targetDate) {
        setLoading(false);
      }
    }
  }, [workspaceId, locationId, locations, employees, selectedDate, settings, setItems, setLoading]);

  // Auto-fetch on mount, date change, or location change
  useEffect(() => {
    fetchAppointments();
  }, [selectedDate, locationId, fetchAppointments]);

  // Filter, sort, and group the items strictly for the selected date & location
  const filteredItems = getFilteredItems(
    items,
    selectedDate || today(),
    locationId,
    searchQuery,
    filterStatus,
    settings.visibleStatuses,
    filterEmployee,
    filterLocation
  );
  const sortedItems = getSortedItems(filteredItems, sortBy, sortOrder);
  const groupedItems = getGroupedItems(sortedItems, groupBy);

  return {
    items: sortedItems,
    rawItems: items,
    groupedItems,
    loading,
    selectedDate,
    searchQuery,
    filterStatus,
    filterEmployee,
    filterLocation,
    sortBy,
    sortOrder,
    groupBy,
    processedIds,
    fetchAppointments,
    setSelectedDate,
    setSearchQuery,
    setFilterStatus,
    setFilterEmployee,
    setFilterLocation,
    setSortBy,
    setSortOrder,
    setGroupBy,
    markProcessed,
    unmarkProcessed,
    isProcessed,
    refresh: () => fetchAppointments(selectedDate),
  };
}

async function enrichAppointments(
  appointments: Appointment[],
  workspaceId: string,
  settings: Settings,
  targetDate: string,
  signal?: AbortSignal
) {
  const store = useAppointmentsStore.getState();
  const concurrency = settings.detailConcurrency || 6;
  const delay = settings.requestDelayMs || 80;

  const bookingIds = appointments
    .map((apt) => apt.primaryBookingId || apt.id)
    .filter(Boolean);

  if (bookingIds.length === 0) return;

  // 1. Look up existing cached details in Dexie
  const cachedMap = await getCachedBookingDetails(bookingIds);
  if (signal?.aborted || useAppointmentsStore.getState().selectedDate !== targetDate) return;

  let currentItems = [...useAppointmentsStore.getState().items];
  let updatedAnyFromCache = false;
  const appointmentsToFetch: Appointment[] = [];

  for (const apt of appointments) {
    const bookingId = apt.primaryBookingId || apt.id;
    const cached = cachedMap[bookingId];
    if (cached) {
      const idx = currentItems.findIndex((a) => a.id === apt.id);
      if (idx !== -1) {
        const updated = {
          ...currentItems[idx],
          customerPhone: cached.phone || currentItems[idx].customerPhone,
          customerEmail: cached.email || currentItems[idx].customerEmail,
          customerName: cached.customerName || currentItems[idx].customerName,
          totalAmount: cached.totalAmount !== undefined ? cached.totalAmount : currentItems[idx].totalAmount,
          salesOrderId: cached.salesOrderId || currentItems[idx].salesOrderId,
          salePaymentStatus: cached.salePaymentStatus || currentItems[idx].salePaymentStatus,
        };

        // Generate message & url if phone is available
        if (updated.customerPhone) {
          const message = messageForAppointment(updated, settings);
          const url = buildWhatsappUrl(updated.customerPhone, message, settings.whatsappMode);
          updated.whatsappMessage = message;
          updated.whatsappUrl = url;
        }

        currentItems[idx] = updated;
        updatedAnyFromCache = true;
      }
    } else {
      appointmentsToFetch.push(apt);
    }
  }

  if (updatedAnyFromCache && !signal?.aborted && useAppointmentsStore.getState().selectedDate === targetDate) {
    store.setItems(currentItems);
    await saveAppointments(currentItems);
  }

  // 2. Fetch details for appointments not found in cache
  for (let i = 0; i < appointmentsToFetch.length; i += concurrency) {
    if (signal?.aborted || useAppointmentsStore.getState().selectedDate !== targetDate) return;

    const batch = appointmentsToFetch.slice(i, i + concurrency);
    const newDetails: CachedBookingDetail[] = [];

    const results = await Promise.allSettled(
      batch.map(async (apt) => {
        const bookingId = apt.primaryBookingId || apt.id;
        try {
          const detail: any = await api.getBookingDetail(bookingId, workspaceId);
          const included = (Array.isArray(detail?.included)
            ? detail.included
            : Array.isArray(detail?.data?.included)
            ? detail.data.included
            : []) as any[];

          // Extract customer info
          const customer = included.find((inc: any) => inc.type === 'customers');
          const customerAttrs = customer?.attributes || {};
          const phoneRaw = customerAttrs['contact-number'] || customerAttrs.telephone || customerAttrs.phone || '';
          const email = customerAttrs.email || '';
          const customerName = customerAttrs.name || `${customerAttrs['first-name'] || ''} ${customerAttrs['last-name'] || ''}`.trim() || undefined;

          // Extract sale info
          const sale = included.find((inc: any) => inc.type === 'sales' || inc.type === 'sale');
          const saleAttrs = sale?.attributes || {};
          const totalAmount = saleAttrs['total-amount'] != null
            ? Number(saleAttrs['total-amount'])
            : (saleAttrs['total-amount-with-tax'] != null ? Number(saleAttrs['total-amount-with-tax']) : undefined);
          const salesOrderId = sale?.id || saleAttrs['sales-order-id'] || apt.salesOrderId;
          const salePaymentStatus = saleAttrs.status || apt.salePaymentStatus;

          // Extract location info if available
          const loc = included.find((inc: any) => inc.type === 'locations');
          const locationName = loc?.attributes?.['internal-name'] || loc?.attributes?.name;

          return {
            id: apt.id,
            bookingId,
            phone: phoneRaw,
            email,
            customerName,
            totalAmount,
            salesOrderId,
            salePaymentStatus,
            locationName,
          };
        } catch {
          return { id: apt.id, bookingId, phone: '', email: '' };
        }
      })
    );

    if (signal?.aborted || useAppointmentsStore.getState().selectedDate !== targetDate) return;

    // Update items in store
    currentItems = [...useAppointmentsStore.getState().items];
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const { id, bookingId, phone, email, customerName, totalAmount, salesOrderId, salePaymentStatus, locationName } = result.value;

        if (phone || email || customerName || totalAmount !== undefined || salesOrderId || salePaymentStatus) {
          newDetails.push({
            bookingId,
            workspaceId,
            phone: phone || '',
            email: email || '',
            customerName,
            totalAmount,
            salesOrderId,
            salePaymentStatus,
            updatedAt: Date.now(),
          });
        }

        const idx = currentItems.findIndex((a) => a.id === id);
        if (idx !== -1) {
          const updated = {
            ...currentItems[idx],
            customerPhone: phone || currentItems[idx].customerPhone,
            customerEmail: email || currentItems[idx].customerEmail,
            customerName: customerName || currentItems[idx].customerName,
            totalAmount: totalAmount !== undefined ? totalAmount : currentItems[idx].totalAmount,
            salesOrderId: salesOrderId || currentItems[idx].salesOrderId,
            salePaymentStatus: salePaymentStatus || currentItems[idx].salePaymentStatus,
            locationName: locationName || currentItems[idx].locationName,
          };

          // Generate message & url if phone is available
          if (updated.customerPhone) {
            const message = messageForAppointment(updated, settings);
            const url = buildWhatsappUrl(updated.customerPhone, message, settings.whatsappMode);
            updated.whatsappMessage = message;
            updated.whatsappUrl = url;
          }

          currentItems[idx] = updated;
        }
      }
    }

    if (newDetails.length > 0) {
      await saveBookingDetails(newDetails);
    }

    if (!signal?.aborted && useAppointmentsStore.getState().selectedDate === targetDate) {
      store.setItems(currentItems);
      await saveAppointments(currentItems);
    }

    // Throttle between batches
    if (i + concurrency < appointmentsToFetch.length) {
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}
